import { SPACE_OVERVIEW, COMPANY_HUB } from '@confluence/named-routes/entry-points/routes';
import { preloadSpaceOverviewId } from '@confluence/content-types-header/entry-points/preloadSpaceOverviewId';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader';
import { preloadConnectWebResources } from '@confluence/fabric-extension-handlers/entry-points/preloadConnectWebResources';
import { preloadSpaceOverview } from '@confluence/space-overview/entry-points/preloadSpaceOverview';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties';
import { preloadAutohighlights } from '@confluence/contextual-reading-aids/entry-points/preloadAutohighlights';
import { preloadShortcutsSmartLinks } from '@confluence/space-shortcuts/entry-points/preloadShortcutsSmartLinks';
import { preloadSideNavigation } from '@confluence/side-navigation/entry-points/preloadSideNavigation';
import { preloadSpaceGuestList } from '@confluence/space-guest-list/entry-points/preloadSpaceGuestList';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points';
import { preloadAudienceSegmentationContext } from '@confluence/custom-sites-extensions/entry-points/preloadAudienceSegmentationContext';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask';
import { getCompanyHubSpaceKeyForSSR } from '@confluence/route-manager/entry-points/getCompanyHubSpaceKeyForSSR';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery';
import { preloadPresenceSettings } from '@confluence/team-presence/entry-points/preloadPresenceSettings';
import { preloadContentMode } from '@confluence/editor-content-mode-button/entry-points/preloadContentMode';
import {
	getSingleParam,
	getSingleParamInt,
} from '@confluence/route-manager-utils/entry-points/getSingleParam';
import type { RouteMatch } from '@confluence/route/entry-points/Route';
import { preloadSpaceViews } from '@confluence/space-views/entry-points/preloadSpaceViews';
import { fg } from '@confluence/feature-gating';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations';
import { preloadWebPanelLocation } from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation';
import { preloadSuperBatchData } from '@confluence/wrm/entry-points/preloadSuperBatchData';
import { expValEquals } from '@confluence/feature-experiments';

const homepageIdPromiseMap = new Map<string, Promise<string | undefined>>();
const homepageIdMap = new Map<string, string | undefined>();

const loadHomepageId = async (spaceKey: string): Promise<string | undefined> => {
	const { result, hasErrors } = await preloadSpaceOverviewId(spaceKey);
	return (!hasErrors && result?.data?.space?.homepageId) || null;
};

export const preloadSpaceOverviewRouteCritical = async (match: RouteMatch) => {
	const { spaceKey } = match.params;
	const homepageId = match.query
		? getSingleParam(match.query, SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY)
		: undefined;

	if (homepageId) return;

	const homepageIdPromise = homepageIdPromiseMap.get(spaceKey) ?? loadHomepageId(spaceKey);

	// Keep both maps in sync - promises and actual values for synchronous access
	homepageIdPromiseMap.set(spaceKey, homepageIdPromise);
	return homepageIdPromise.then((homepageId) => {
		homepageIdMap.set(spaceKey, homepageId);
	});
};

export const preloadSpaceOverviewRoute = async (
	match: RouteMatch,
	previousMatch: RouteMatch | null,
	_url: string,
) => {
	const { cloudId, isLicensed, userId, activationId } = getPreloaderFnContext();
	const { spaceKey } = match.params;
	const initialHomepageId = match.query
		? getSingleParam(match.query, SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY)
		: undefined;

	const tasks: Promise<any>[] = [];

	if (spaceKey) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			// Only load data for navigation in React SSR since we are delaying them in the SPA
			tasks.push(
				preloadShortcutsSmartLinks(spaceKey),
				preloadSideNavigation(spaceKey),
				// preloadSideNavigationNeedsContentId is not called here because we can't guarantee content id
				// It is called in preloadSpaceOverview
				preloadSpaceGuestList(spaceKey),
				preloadSpaceViews(spaceKey, isLicensed),
			);
		}
	}

	const isCompanyHub = match.name === COMPANY_HUB.name;
	const companyHubSpaceKey = isCompanyHub ? await getCompanyHubSpaceKeyForSSR() : undefined;
	const versionOverride =
		companyHubSpaceKey && match.query ? getSingleParamInt(match.query, 'versionOverride') : null;

	const homepageId = initialHomepageId || homepageIdMap.get(spaceKey ?? companyHubSpaceKey);

	tasks.push(
		preloadSpaceOverview({
			spaceKey: spaceKey ?? companyHubSpaceKey,
			homepageId,
			homepageIdPromise: homepageIdPromiseMap.get(spaceKey ?? companyHubSpaceKey),
			previousMatch,
			versionOverride,
		}),
	);

	if (
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		expValEquals('cc_editor_ai_content_mode', 'variant', 'test') &&
		fg('platform_editor_content_mode_button_mvp') &&
		homepageId
	) {
		tasks.push(preloadContentMode({ contentId: homepageId, isDraft: true, useCache: true }));
	}

	tasks.push(
		preloadPageTitleContentProperties({
			contentId: homepageId || '',
			contentIdPromise: homepageIdPromiseMap.get(spaceKey ?? companyHubSpaceKey),
			spaceKey: spaceKey ?? companyHubSpaceKey,
		}),
	);

	if (
		fg('content_early_connect_iframe_initialization') ||
		fg('webpanel_early_connect_iframe_initialization')
	) {
		// Preload superbatch
		tasks.push(
			prepareGuardExceptionTask('Superbatch', () =>
				preloadSuperBatchData({ preloadSuperBatchForSSR: true }),
			),
		);

		// Preload Connect web resources
		tasks.push(
			prepareGuardExceptionTask('Connect Web Resources', () => preloadConnectWebResources()),
		);
	}

	tasks.push(preloadSpaceDetail(spaceKey));

	if (isCompanyHub && userId) {
		tasks.push(
			preloadAudienceSegmentationContext({
				accountId: userId,
			}),
		);
	}

	// Presence Avatar Visibility
	tasks.push(preloadPresenceSettings({ cloudId, spaceKey }));

	// Space Overview's homepageId is needed for the content types header, but may not be in the URL
	// depending on which route was used.  skip preloading if it's not in the url because sequential calls block SSR
	if (!isCompanyHub && homepageId && fg('confluence_frontend_content_wrapper')) {
		tasks.push(preloadContentTypesHeader(homepageId, fg('confluence_frontend_content_wrapper')));
		// Standalone Restrictions button needs to have its data preloaded)
		tasks.push(preloadPageRestrictionsQuery(homepageId, false));
	}

	// Unified Share and Restrict Button
	if (!isCompanyHub && homepageId && fg('confluence_frontend_usd_ssr')) {
		tasks.push(preloadShareAndRestrictButtonQuery(homepageId));
	}

	// Preload definition autohighlights
	if (homepageId && fg('confluence_frontend_ssr_autohighlights')) {
		tasks.push(
			preloadAutohighlights({
				contentId: homepageId,
				cloudId,
				activationId,
				contentType: 'page',
			}),
		);
	}

	// Preload media token for cover image
	if (homepageId && fg('confluence_frontend_cover_image_ssr')) {
		tasks.push(preloadMediaToken(homepageId));
	}

	if (homepageId) {
		tasks.push(
			preloadWebPanelLocation({ contentId: homepageId, location: ATL_GENERAL }),
			preloadWebPanelLocation({ contentId: homepageId, location: ATL_FOOTER }),
		);
	}

	if (
		!isCompanyHub &&
		!homepageId &&
		(fg('confluence_frontend_usd_ssr') || fg('confluence_frontend_content_wrapper'))
	) {
		// Unified Share and Restrict Button
		if (expValEquals('fix_preload_share_and_restrict_query_chaining_', 'cohort', 'test')) {
			tasks.push(preloadShareAndRestrictButtonQuery(undefined, spaceKey));
		}

		//moved chained queries to the bottom to unblock other queries needed to preload & to combine the contentId query
		const homepageIdPromise = homepageIdPromiseMap.get(spaceKey) || loadHomepageId(spaceKey);
		await homepageIdPromise.then((contentId) => {
			if (fg('confluence_frontend_content_wrapper')) {
				tasks.push(preloadContentTypesHeader(contentId, fg('confluence_frontend_content_wrapper')));
				tasks.push(preloadPageRestrictionsQuery(contentId ?? '', false));
			}

			if (
				!expValEquals('fix_preload_share_and_restrict_query_chaining_', 'cohort', 'test') &&
				fg('confluence_frontend_usd_ssr')
			) {
				tasks.push(preloadShareAndRestrictButtonQuery(contentId ?? ''));
			}

			if (contentId) {
				// Preload web panel atl_general
				tasks.push(
					preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
					preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
				);

				// Preload media token for cover image
				if (fg('confluence_frontend_cover_image_ssr')) {
					tasks.push(preloadMediaToken(contentId));
				}
			}
		});
	}

	return waitForAllReturnWhenError(tasks);
};

// WARNING: Exposed for testing only
export const _clearHomepageIdMaps = () => {
	homepageIdMap.clear();
	homepageIdPromiseMap.clear();
};
